import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import SyncProgramModal from '../components/SyncProgramModal';

const SyncProgramModalContext = createContext(null);

const PROGRAM_PATH_RE = /^\/synchronization\/program\/(\d+)\/(\d+)/;

export function SyncProgramModalProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalState, setModalState] = useState({
    open: false,
    animalId: null,
    programId: null,
    mode: 'view',
  });

  const invalidateSyncQueries = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['sync-programs'] });
    await queryClient.invalidateQueries({ queryKey: ['animal-sync-programs'] });
    await queryClient.invalidateQueries({ queryKey: ['daily-operations'] });
    await queryClient.invalidateQueries({ queryKey: ['animal-breedings'] });
  }, [queryClient]);

  const openSyncProgram = useCallback(({ animalId, programId, mode = 'view' }) => {
    if (!animalId || !programId) return;
    setModalState({
      open: true,
      animalId: Number(animalId),
      programId: Number(programId),
      mode: mode === 'manage' ? 'manage' : 'view',
    });
  }, []);

  const closeSyncProgram = useCallback(() => {
    setModalState({
      open: false,
      animalId: null,
      programId: null,
      mode: 'view',
    });
  }, []);

  useEffect(() => {
    const match = location.pathname.match(PROGRAM_PATH_RE);
    if (!match) return;

    const animalId = Number(match[1]);
    const programId = Number(match[2]);
    const search = new URLSearchParams(location.search);
    const mode = search.get('action') === 'manage' ? 'manage' : 'view';
    const from = search.get('from');

    let returnPath = location.state?.returnPath || '/synchronization';
    if (!location.state?.returnPath) {
      if (from === 'calendar') returnPath = '/synchronization/calendar';
      else if (from === 'history') returnPath = '/synchronization/history';
    }

    setModalState({
      open: true,
      animalId,
      programId,
      mode,
    });

    navigate(returnPath, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  const value = useMemo(
    () => ({ openSyncProgram, closeSyncProgram }),
    [openSyncProgram, closeSyncProgram]
  );

  return (
    <SyncProgramModalContext.Provider value={value}>
      {children}
      <SyncProgramModal
        open={modalState.open}
        animalId={modalState.animalId}
        programId={modalState.programId}
        mode={modalState.mode}
        onClose={closeSyncProgram}
        onSaved={invalidateSyncQueries}
      />
    </SyncProgramModalContext.Provider>
  );
}

export function useSyncProgramModal() {
  const context = useContext(SyncProgramModalContext);
  if (!context) {
    throw new Error('useSyncProgramModal must be used within SyncProgramModalProvider');
  }
  return context;
}
